import React, { useState, useEffect } from "react";
import BillsDetails from "./BillsDetails";
import { database } from "../../../firebase";
import {
  equalTo,
  get,
  onValue,
  orderByChild,
  query,
  ref,
} from "firebase/database";
import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";

const Bills = ({ user }) => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [getCompanyId, setCompanyId] = useState("");
  const [getUser, setUser] = useState("");
  const [companyCreateAt, setCompanyCreateAt] = useState("");
  const [companyExpiredAt, setCompanyExpiredAt] = useState(""); // Company creation date
  const [monthlyUserCounts, setMonthlyUserCounts] = useState({}); // Track monthly user counts
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rate, setRate] = useState(); // State to manage rate per user
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billDetails, setBillDetails] = useState({
    monthYear: "",
    count: 0,
    rate: 0,
    totalAmount: 0,
  }); // New state to hold selected bill details
  const [isPayButtonEnabled, setIsPayButtonEnabled] = useState(false);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    if (user) {
      fetchData(user);
      setRate(5); // Assuming the rate is set here
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Main function to fetch all data
  const fetchData = async (user) => {
    setLoading(true);
    try {
      const userId = user?.uid;
      if (!userId) return;

      // Fetch user info
      const userRef = ref(database, `users/${userId}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();
        const companyId = userData.companyId;
        setUser(userData);
        setCompanyId(companyId);
        // Fetch company data and user counts
        await Promise.all([
          fetchCompany(companyId),
          fetchUsers(companyId),
          fetchBills(companyId),
        ]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompany = async (companyId) => {
    try {
      if (!companyId) return;
      const companyRef = ref(database, `companies/${companyId}`);
      const snapshot = await get(companyRef);

      if (snapshot.exists()) {
        const companyData = snapshot.val();
        const createdAt = companyData.createdAt;
        const expiredAt = companyData.expiredAt;
        const isEnabled = checkPayButtonEnabled(expiredAt);
        setIsPayButtonEnabled(isEnabled);
        // Safely format the date
        if (createdAt || expiredAt) {
          const createddate = new Date(companyData.createdAt);
          const expiredDate = new Date(companyData.expiredAt);
          if (!isNaN(createddate)) {
            const formattedDate = createddate.toLocaleDateString("default", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
            setCompanyCreateAt(formattedDate);
          } else {
            setCompanyCreateAt("Invalid Date");
          }
          if (!isNaN(expiredDate)) {
            const formattedExpiredDate = expiredDate.toLocaleDateString(
              "default",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            );
            setCompanyExpiredAt(formattedExpiredDate);
          } else {
            setCompanyExpiredAt("Invalid Date");
          }
        } else {
          setCompanyCreateAt("Not Available");
        }
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      setCompanyCreateAt("Not Available");
    }
  };
  const checkPayButtonEnabled = (expiredAt) => {
    const expiredDate = new Date(expiredAt);
    const currentDate = new Date();
    const timeDifference = expiredDate - currentDate;
    const daysDifference = timeDifference / (1000 * 3600 * 24);
    return daysDifference <= 3;
  };

  const fetchUsers = async (companyId) => {
    try {
      // Reference to the specific company in the database
      const companyRef = ref(database, `companies/${companyId}`);
      const companySnapshot = await get(companyRef);

      if (companySnapshot.exists()) {
        const company = companySnapshot.val();
        const expiredAt = new Date(company.expiredAt); // Convert to Date object
        const beforeExpiredAt = new Date(expiredAt);
        beforeExpiredAt.setMonth(expiredAt.getMonth() - 1); // One month before expiredAt

        const userRef = ref(database, "users");
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          const usersData = {};
          let totalCount = 0; // Initialize total count

          // Iterate over the snapshot to extract user data
          snapshot.forEach((childSnapshot) => {
            const userData = childSnapshot.val();

            // Check if the user has the role "User" and ensure the necessary data exists
            if (userData.role === "User" && userData.companyId === companyId) {
              const createdAt = userData.createdAt; // Use the user's createdAt
              const createdAtDate = new Date(createdAt);
              if (
                !isNaN(createdAtDate) &&
                createdAtDate >= beforeExpiredAt &&
                createdAtDate <= expiredAt
              ) {
                debugger;
                const monthYear = `${
                  createdAtDate.getMonth() + 1
                }/${createdAtDate.getFullYear()}`;

                // Update the count for the specific month/year
                if (usersData[monthYear]) {
                  usersData[monthYear] += 1;
                } else {
                  usersData[monthYear] = 1;
                }

                // Increment the total count
                totalCount += 1;
              }
            }
          });

          // Update the state with the filtered user counts and total count
          setMonthlyUserCounts(usersData);
          setTotalUserCount(totalCount); // Set total count as a separate state
        } else {
          console.log("No data available in 'users' node.");
        }
      } else {
        console.log("No data available for the specified company.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchBills = async (companyId) => {
    const billsRef = ref(database, "bills");
    const billsQuery = query(
      billsRef,
      orderByChild("companyId"),
      equalTo(companyId)
    );

    onValue(billsQuery, (snapshot) => {
      const billsData = [];
      snapshot.forEach((childSnapshot) => {
        billsData.push(childSnapshot.val());
      });
      setBills(billsData);
    });
  };

  // Popup handlers
  const handleButtonClick = () => setShowPopup(true);
  const handleClose = () => {
    setShowPopup(false);
  };

  const handlePayClick = (companyExpiredAt, totalUserCount) => {
    if (!monthlyUserCounts || typeof totalUserCount !== "number") {
      console.error(
        "Invalid monthly user count:",
        companyExpiredAt,
        totalUserCount
      );
      return;
    }

    console.error(
      "Company ExpiredAt user count:",
      companyExpiredAt,
      totalUserCount
    );

    // Ensure companyExpiredAt is a valid Date object
    const expiredDate = new Date(companyExpiredAt);
    debugger;
    // Check if the date is valid
    if (isNaN(expiredDate)) {
      console.error("Invalid date:", companyExpiredAt);
      return;
    }
    const monthYear = expiredDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });

    const count = totalUserCount || 0;

    const rate = 10;
    const totalAmount = totalUserCount * rate;

    setBillDetails({
      monthYear: monthYear,
      count,
      rate,
      totalAmount,
    });
    console.log("Bill details updated", {
      monthYear,
      count,
      rate,
      totalAmount,
    });

    setIsModalOpen(true);
  };

  const handlePayNow = (count) => {
    const userCount = count;
    const totalAmount = count * rate;
    const company = getCompanyId;
    const user = getUser;
    setIsModalOpen(false);
    navigate("/payment", { state: { userCount, totalAmount, company, user } });
  };
  return (
    <div className="flex flex-col mx-4 bg-white px-8 rounded-lg h-4/5">
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* Company Creation Date */}
          <div className="mt-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="mb-2">
                  <strong>Register Date:</strong> {companyCreateAt.toString()}
                </p>
                <p>
                  <strong>Expired Date:</strong> {companyExpiredAt.toString()}
                </p>
              </div>

              {/* Pay button */}

              {isPayButtonEnabled && (
                <Button
                  variant="contained"
                  color="error"
                  sx={{ mb: 2, px: 3, py: 1 }}
                  startIcon={<PaymentIcon />} // Optional: Adds an icon
                  onClick={() =>
                    handlePayClick(companyExpiredAt, totalUserCount)
                  }
                >
                  Pay
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              mb: 2,
              ml: "auto",
              display: "flex",
              backgroundColor: "#90EE90", // Light Green
              "&:hover": { backgroundColor: "#76c776" }, // Slightly darker on hover
              color: "black",
            }}
            startIcon={<ReceiptIcon />}
            onClick={handleButtonClick}
          >
            Show Bills Details
          </Button>

          {showPopup && <BillsDetails onClose={handleClose} />}
          {/* User Counts Table */}
          <div>
            <TableContainer component={Paper} className="hidden lg:flex">
              <Table stickyHeader>
                {/* Table Header */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell align="center">S.N</TableCell>
                    <TableCell>Bill Number</TableCell>
                    <TableCell>Renew Date</TableCell>
                    <TableCell align="center">Number Users</TableCell>
                    <TableCell align="right">Paid Amount</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {bills.length > 0 ? (
                    bills.map((bill, index) => (
                      <TableRow key={bill.Id} hover>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>{bill.Id}</TableCell>
                        <TableCell>
                          {new Date(bill.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </TableCell>
                        <TableCell align="center">{bill.userNumber}</TableCell>
                        <TableCell align="right">${bill.Amount}</TableCell>
                        <TableCell align="center" sx={{ color: "green" }}>
                          {bill.paymentStatus}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="subtitle1" color="textSecondary">
                          No bills found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Grid View for Mobile */}
            <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {bills.length > 0 ? (
                bills.map((bill, index) => (
                  <Card
                    key={bill.Id}
                    className="shadow-lg flex flex-col justify-between"
                  >
                    <CardContent className="text-center space-y-2">
                      <p className="text-gray-500 font-bold">
                        S.N: {index + 1}
                      </p>
                      <p className="text-gray-500">
                        Bill Number:{" "}
                        <span className="font-semibold">{bill.Id}</span>
                      </p>
                      <p className="text-gray-500">
                        Renew Date:{" "}
                        <span className="font-semibold">
                          {new Date(bill.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </span>
                      </p>
                      <p className="text-gray-500">
                        Number Users:{" "}
                        <span className="font-semibold">{bill.userNumber}</span>
                      </p>
                      <p className="text-gray-500">
                        Paid Amount:{" "}
                        <span className="font-semibold text-green-600">
                          ${bill.Amount}
                        </span>
                      </p>
                      <p
                        className={`text-sm font-semibold ${
                          bill.paymentStatus === "Paid"
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {bill.paymentStatus}
                      </p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-1 text-center text-gray-500">
                  No bills found.
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* Modal */}
      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={handleClose}
          maxWidth="xs"
          fullWidth
        >
          {/* Title */}
          <DialogTitle>Review Bill</DialogTitle>

          {/* Content */}
          <DialogContent dividers>
            <Typography variant="body1">
              <strong>Month/Year:</strong> {billDetails?.monthYear}
            </Typography>

            {/* Table */}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>User Count</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Rate</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{billDetails?.count}</TableCell>
                  <TableCell>${billDetails?.rate}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Typography variant="body1" sx={{ marginTop: 2 }}>
              <strong>Total Amount:</strong> ${billDetails?.totalAmount}
            </Typography>
          </DialogContent>

          {/* Actions */}
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => handlePayNow(billDetails?.count)}
              variant="contained"
              sx={{
                backgroundColor: "#90EE90", // Light Green
                "&:hover": { backgroundColor: "#76c776" }, // Darker on hover
              }}
            >
              Pay Now
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default Bills;
